/**
 * Hook for fetching OpenTable verification code
 * Used during OpenTable integration onboarding
 */

import { useState, useCallback } from 'react'
import api from '@/lib/api'

interface OpenTableVerificationResponse {
  partner_id: string
  assistant_id: number
  assistant_name: string
}

interface UseOpenTableVerificationResult {
  verificationCode: OpenTableVerificationResponse | null
  loading: boolean
  error: string | null
  rotating: boolean
  rotateError: string | null
  fetchVerificationCode: (assistantId: number) => Promise<void>
  rotatePartnerId: (assistantId: number) => Promise<boolean>
}

export const useOpenTableVerification = (): UseOpenTableVerificationResult => {
  const [verificationCode, setVerificationCode] =
    useState<OpenTableVerificationResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [rotating, setRotating] = useState(false)
  const [rotateError, setRotateError] = useState<string | null>(null)

  const fetchVerificationCode = useCallback(async (assistantId: number) => {
    setLoading(true)
    setError(null)

    try {
      const response = await api.get<OpenTableVerificationResponse>(
        `/opentable/verification-code/${assistantId}`
      )
      setVerificationCode(response.data)
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.detail || err.message || 'Failed to fetch verification code'
      setError(errorMessage)
      setVerificationCode(null)
    } finally {
      setLoading(false)
    }
  }, [])

  const rotatePartnerId = useCallback(async (assistantId: number): Promise<boolean> => {
    setRotating(true)
    setRotateError(null)

    try {
      const response = await api.post<OpenTableVerificationResponse>(
        `/opentable/rotate-partner-id/${assistantId}`
      )
      setVerificationCode(response.data)
      return true
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.detail || err.message || 'Failed to rotate partner ID'
      setRotateError(errorMessage)
      return false
    } finally {
      setRotating(false)
    }
  }, [])

  return {
    verificationCode,
    loading,
    error,
    rotating,
    rotateError,
    fetchVerificationCode,
    rotatePartnerId,
  }
}
